import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

const SCROLL_THRESHOLD = 2

const DEFAULT_SHADOW_GRADIENT =
  "from-background via-background/80 to-background/50"

interface ScrollAreaProps extends React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
> {
  scrollShadow?: boolean
  /**
   * Gradient classes for shadow overlay (e.g. from-card via-card/80 to-card/50).
   * Use when the scroll content has a different background than the page.
   */
  scrollShadowClassName?: string
}

function useScrollShadow(viewportRef: React.RefObject<HTMLDivElement | null>) {
  const [visibility, setVisibility] = React.useState({
    top: false,
    bottom: false,
  })

  const updateVisibility = React.useCallback(() => {
    const el = viewportRef.current
    if (!el) return

    const { scrollTop, scrollHeight, clientHeight } = el
    const isScrollable = scrollHeight > clientHeight + SCROLL_THRESHOLD

    setVisibility((prev) => {
      const next = {
        top: isScrollable && scrollTop > SCROLL_THRESHOLD,
        bottom:
          isScrollable &&
          scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD,
      }
      if (prev.top === next.top && prev.bottom === next.bottom) return prev
      return next
    })
  }, [])

  React.useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    updateVisibility()

    const resizeObserver = new ResizeObserver(updateVisibility)
    resizeObserver.observe(el)

    return () => resizeObserver.disconnect()
  }, [updateVisibility])

  return { visibility, updateVisibility }
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      className,
      children,
      scrollShadow = false,
      scrollShadowClassName,
      ...props
    },
    ref
  ) => {
    const viewportRef = React.useRef<HTMLDivElement>(null)
    const { visibility, updateVisibility } = useScrollShadow(viewportRef)
    const shadowGradient = scrollShadowClassName ?? DEFAULT_SHADOW_GRADIENT

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          onScroll={scrollShadow ? updateVisibility : undefined}
          className="!block h-full w-full min-w-0 overflow-hidden rounded-[inherit] [&>div]:!block [&>div]:min-w-0"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {scrollShadow && (
          <>
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-x-0 top-0 z-10 h-10 shrink-0 bg-gradient-to-b transition-opacity duration-300 ease-out",
                shadowGradient,
                visibility.top ? "opacity-100" : "opacity-0"
              )}
            />
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 shrink-0 bg-gradient-to-t transition-opacity duration-300 ease-out",
                shadowGradient,
                visibility.bottom ? "opacity-100" : "opacity-0"
              )}
            />
          </>
        )}
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    )
  }
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none transition-colors select-none",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-px",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-px",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="bg-muted-foreground/40 hover:bg-muted-foreground/50 relative flex-1 rounded-full transition-colors" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
